/* eslint-disable camelcase */
import {
  takeLatest,
  put,
  delay,
  select,
  all,
  call,
  take,
  takeEvery,
  fork,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { logEvent, setUserId } from 'firebase/analytics';
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updatePassword,
  updateProfile,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendEmailVerification,
  applyActionCode,
  reload,
} from 'firebase/auth';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

import { auth, db, analytics, storage } from 'firebaseConfig';

import {
  currentProject,
  projects as allProjects,
} from 'modules/Projects/store/selectors';
import { currentPage } from 'modules/Web/store/selectors';
import { allTeams } from 'modules/Team/store/selectors';

import {
  convertDownloadURLToGS,
  createDocumentChangeChannel,
  getUserPhotoURL,
} from 'utils';

import {
  showSnackbar,
  hideSnackbar,
  toggleAppLoading,
  contactUpdate,
} from 'modules/App/store/actions';
import {
  allProjectsRequest,
  resetProject,
  cancelProjectsListener,
  allProjectsSuccess,
} from 'modules/Projects/store/actions';
import { resetIllustration } from 'modules/Illustration/store/actions';
import { resetWeb } from 'modules/Web/store/actions';
import {
  resetTeam,
  cancelTeamsListener,
  teamsRequest,
  teamsSuccess,
} from 'modules/Team/store/actions';

import { defaultEmailPreferences } from 'utils/contentProvider';
import {
  actions,
  signOutRequest,
  signOutSuccess,
  toggleLoading,
  showError,
  hideError,
  changePasswordSuccess,
  updateProfileSuccess,
  // resetPasswordEmailSuccess,
  signInSuccess,
  notificationsSuccess,
  toggleNotificationsLoading,
  listenNotifications,
  forgotPasswordUpdate,
  userActionUpdate,
  fetchCurrentUser,
  feedbackUpdate,
  signupUpdate,
  toggleReauth,
  subscriptionsSuccess,
  billingDetailsRequest,
  billingDetailsSuccess,
  fetchCurrentIntegrationsSuccess,
} from './actions';

import * as selectors from './selectors';
import { getContent, getLink } from '../../../utils/notifications';

const googleAuthProvider = new GoogleAuthProvider();

function* observeUserSaga({ navigate }) {
  const authChannel = eventChannel((emitter) =>
    onAuthStateChanged(auth, (user) => emitter(user || 'null')),
  );

  while (true) {
    const user = yield take(authChannel);

    if (user !== 'null') {
      const {
        displayName,
        email,
        emailVerified,
        phoneNumber,
        photoURL,
        uid,
        metadata: { creationTime },
      } = user;

      setUserId(analytics, uid);

      yield put(
        signInSuccess({
          displayName,
          email,
          emailVerified,
          phoneNumber,
          photoURL,
          uid,
          createdAt: Timestamp.fromDate(new Date(creationTime)),
        }),
      );
      yield put(fetchCurrentUser(uid));
      yield put(teamsRequest());
      yield put(listenNotifications());
      navigate(
        window.location.pathname === '/'
          ? `/projects${window.location.search}`
          : `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
    } else {
      setUserId(analytics, '');
      yield put(cancelProjectsListener());
      yield put(cancelTeamsListener());

      if (
        window.location.pathname === '/' &&
        window.location.search === '?delete=true'
      )
        navigate('/');
      else if (
        !window.location.pathname.startsWith('/forgot-password') &&
        !window.location.pathname.startsWith('/user-action') &&
        !window.location.pathname.startsWith('/signup') &&
        !window.location.pathname.startsWith('/share') &&
        !window.location.pathname.startsWith('/preview') &&
        window.location.pathname !== '/'
      )
        navigate(`/?goto=${window.location.pathname}`);
    }

    yield put(toggleAppLoading(false));
  }
}

export function* watchObserveAuth() {
  yield takeLatest(actions.OBSERVE_USER, observeUserSaga);
}

function* signInSaga({ payload, navigate }) {
  yield put(toggleLoading(true));
  yield put(hideError());
  try {
    const { reauth } = yield select(selectors.user);

    yield setPersistence(
      auth,
      payload.persist ? browserLocalPersistence : browserSessionPersistence,
    );

    const { user } = yield signInWithEmailAndPassword(
      auth,
      payload.email,
      payload.password,
    );

    if (reauth === true) {
      yield put(toggleReauth());
      navigate('/profile#delete-account');
    } else if (payload?.successURL) {
      if (
        payload?.addToProject &&
        user.uid &&
        payload.addToProject.users.some(({ uid }) => uid === user.uid) === false
      ) {
        yield updateDoc(doc(db, 'projects', payload.addToProject.id), {
          users: arrayUnion({
            access: 'comment',
            uid: user.uid,
          }),
        });
      }

      navigate(payload.successURL);
    } else navigate('/projects');
    yield put(toggleLoading(false));
    logEvent(analytics, 'user_sign_in_email', {
      event_user: (user && user.uid) || null,
    });
  } catch (error) {
    yield put(showError(error));
    yield put(toggleLoading(false));
  }
}

export function* watchSignIn() {
  yield takeLatest(actions.SIGN_IN_REQUEST, signInSaga);
}

function* signInGoogleRequestSaga({ payload, navigate }) {
  try {
    const { reauth } = yield select(selectors.user);
    const { user, additionalUserInfo } = yield signInWithPopup(
      auth,
      googleAuthProvider,
    );

    if (reauth === true) {
      yield put(toggleReauth());
      navigate('/profile#delete-account');
    } else if (payload?.successURL) {
      if (
        payload?.addToProject &&
        user.uid &&
        payload.addToProject.users.some(({ uid }) => uid === user.uid) === false
      ) {
        yield updateDoc(doc(db, 'projects', payload.addToProject.id), {
          users: arrayUnion({
            access: 'comment',
            uid: user.uid,
          }),
        });
      }

      navigate(payload.successURL);
    } else navigate('/projects');

    if (additionalUserInfo.isNewUser === true) {
      yield axios.post(
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
        {
          from: `Team ruttl <support@ruttl.com>`,
          to: user.email,
          subject: `Welcome to ruttl!`,
          template: window.innerWidth < 979 ? 'user-signup-mobile' : 'welcome',
        },
      );
      if (window.dataLayer)
        window.dataLayer.push({
          event: 'app-new-signup',
          uid: user?.uid || '',
        });
    }
    logEvent(
      analytics,
      window.innerWidth < 979
        ? 'user_sign_in_google_mobile'
        : 'user_sign_in_google',
      {
        event_user: (user && user.uid) || null,
      },
    );
  } catch (error) {
    yield put(signupUpdate({ error }));
  }
}

export function* watchSignInGoogle() {
  yield takeLatest(actions.SIGN_IN_GOOGLE_REQUEST, signInGoogleRequestSaga);
}

function* signUpSaga({ payload }) {
  yield put(signupUpdate({ error: '' }));

  if (payload.email && payload.password) {
    try {
      const { user } = yield createUserWithEmailAndPassword(
        auth,
        payload.email,
        payload.password,
      );
      yield sendEmailVerification(user);

      // Add to project

      if (payload.projectID) {
        yield updateDoc(doc(db, 'projects', payload.projectID), {
          users: arrayUnion({
            access: 'comment',
            uid: user.uid,
          }),
        });
      }

      logEvent(
        analytics,
        window.innerWidth < 979
          ? 'user_sign_up_email_mobile'
          : 'user_sign_up_email',
        {
          event_user: (user && user.uid) || null,
        },
      );
      if (window.fbq) window.fbq('trackCustom', 'SignUp');
      if (window.dataLayer)
        window.dataLayer.push({
          event: 'app-new-signup',
          uid: user?.uid || '',
        });
    } catch (error) {
      yield put(signupUpdate({ error }));
    }
  }
}

export function* watchSignUp() {
  yield takeLatest(actions.SIGN_UP_REQUEST, signUpSaga);
}

function* signOutSaga({ payload, navigate }) {
  const { uid } = auth.currentUser;

  yield auth.signOut();
  navigate((payload && payload.goTo) || '/');
  yield put(signOutSuccess());
  yield put(resetProject());
  yield put(resetIllustration());
  yield put(resetWeb());
  yield put(resetTeam());
  logEvent(analytics, 'user_sign_out', { event_user: uid || null });
}

export function* watchSignOut() {
  yield takeLatest(actions.SIGN_OUT_REQUEST, signOutSaga);
}

function* changePasswordSaga({ payload, navigate }) {
  if (payload.length > 7) {
    try {
      yield updatePassword(auth.currentUser, payload);
      yield put(changePasswordSuccess(true));
      yield put(showSnackbar('Password updated.'));
      logEvent(analytics, 'user_password_update', {
        event_user: (auth && auth.currentUser && auth.currentUser.uid) || null,
      });
      yield delay(1000);
      yield put(changePasswordSuccess(false));
      yield delay(4000);
      yield put(hideSnackbar());
    } catch (error) {
      if (error.code === 'auth/requires-recent-login')
        yield put(signOutRequest({ goTo: '/?goto=/profile' }, navigate));
    }
  }
}

export function* watchChangePassword() {
  yield takeLatest(actions.CHANGE_PASSWORD_REQUEST, changePasswordSaga);
}

function* updateUserAvatarGlobally(uid, photoURL) {
  const projects = yield select(allProjects);
  const teams = yield select(allTeams);

  if (projects?.length > 0) {
    const newProjects = projects.map((p) => ({
      ...p,
      users: p.users.map((u) =>
        u.uid === uid ? { ...u, photoURL: getUserPhotoURL(photoURL) } : u,
      ),
    }));

    yield put(allProjectsSuccess(newProjects));
  }

  if (teams.length > 0) {
    const newTeams = teams.map((t) => ({
      ...t,
      users: t.users.map((u) =>
        u.uid === uid ? { ...u, photoURL: getUserPhotoURL(photoURL) } : u,
      ),
    }));

    yield put(teamsSuccess(newTeams));
  }
}

function* updateProfileSaga({ payload, callback }) {
  const { user: currentUser } = yield select(selectors.user);
  const token = yield auth.currentUser.getIdToken();

  const updateObj = {
    ...(payload.displayName ? { displayName: payload.displayName } : {}),
    ...(payload.pendingEmail ? { pendingEmail: payload.pendingEmail } : {}),
  };

  yield put(toggleLoading(true));

  // Removes image switching glitch while uploading to storage.
  yield put(updateProfileSuccess({ ...currentUser, ...payload, ...updateObj }));

  if (payload.photoURL) {
    if (payload.photoURL.startsWith('data:')) {
      const snapshot = yield uploadString(
        ref(storage, `avatars/${currentUser.uid}`),
        payload.photoURL,
        'data_url',
      );

      const downloadURL = yield getDownloadURL(snapshot.ref);

      updateObj.photoURL = convertDownloadURLToGS(downloadURL);
    } else {
      updateObj.photoURL = payload.photoURL;
    }
  }

  // Update store with photoURL storage url
  yield put(updateProfileSuccess({ ...currentUser, ...payload, ...updateObj }));
  if (Object.keys(updateObj).length > 0) {
    const { pendingEmail, ...authUserObject } = updateObj;

    // Avoid sending `pendingEmail` to Auth.
    yield updateProfile(auth.currentUser, authUserObject);
    yield updateDoc(doc(db, 'users', auth.currentUser.uid), updateObj);
  }

  // Send verify email to new email if email change.
  if (payload.pendingEmail)
    yield call(axios.post, `${window.location.origin}/email-change`, {
      token,
    });

  yield put(toggleLoading(false));
  logEvent(analytics, 'user_profile_update', {
    event_user: (auth && auth.currentUser && auth.currentUser.uid) || null,
  });
  yield put(showSnackbar('Profile updated.'));
  yield call(updateUserAvatarGlobally, currentUser.uid, updateObj.photoURL);
  if (callback) callback();
  yield delay(5000);
  yield put(hideSnackbar());
}

export function* watchUpdateProfile() {
  yield takeLatest(actions.UPDATE_PROFILE, updateProfileSaga);
}

function* checkIncompleteSubscription(subscription, stripeCustomerID) {
  if (
    subscription.status === 'past_due' ||
    subscription.status === 'incomplete' ||
    subscription.status === 'unpaid' ||
    subscription.pending_update
  ) {
    let { data: paymentIntents } = yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeRetreivePaymentIntents`,
      { id: stripeCustomerID },
    );

    paymentIntents = paymentIntents.data.filter(
      ({ status }) =>
        status === 'requires_action' || status === 'requires_payment_method',
    );

    if (paymentIntents.length > 0)
      return {
        ...subscription,
        paymentIntents,
      };
  }

  return subscription;
}

function* getUserStripeSubscriptions(stripeCustomerID) {
  let subscriptions = [];
  let updatedSubscription = null;

  try {
    const { data } = yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeRetrieveSubscriptions`,
      { id: stripeCustomerID },
    );

    subscriptions = data;
  } catch (err) {
    //
  }

  if (subscriptions.length > 0)
    updatedSubscription = yield checkIncompleteSubscription(
      subscriptions[0],
      stripeCustomerID,
    );

  if (updatedSubscription)
    yield put(subscriptionsSuccess([updatedSubscription]));
}

function* fetchCurrentUserSaga({ payload, isRetry = false }) {
  let { user: currentUser } = yield select(selectors.user);
  const userSnap = yield getDoc(doc(db, 'users', payload));

  if (userSnap.exists()) {
    const dbUser = { ...userSnap.data(), uid: userSnap.id };

    if (dbUser.notifications) delete dbUser.notifications;

    const isNotEqual = Object.keys(currentUser)
      .filter((i) => typeof currentUser[i] === 'string' && i !== 'photoURL')
      .some((i) => currentUser[i] !== dbUser[i]);

    if (isNotEqual || !dbUser.onboarding || !dbUser.emailPreferences) {
      const { uid, ...updateUser } = { ...currentUser };

      yield updateDoc(doc(db, 'users', payload), {
        ...updateUser,
        onboarding: dbUser.onboarding || {},
        emailPreferences: dbUser.emailPreferences || defaultEmailPreferences,
      });
    }

    if (typeof dbUser.privilege === 'string') {
      yield put(
        subscriptionsSuccess([
          {
            status: 'active',
            metadata: { uid: currentUser.uid, planID: dbUser.privilege },
            isPrivilege: true,
          },
        ]),
      );
    } else if (typeof dbUser.stripeCustomerID === 'string' && !dbUser.privilege)
      yield call(getUserStripeSubscriptions, dbUser.stripeCustomerID);

    currentUser = yield select(selectors.user);

    yield put(
      signInSuccess({
        ...(dbUser.notificationSound !== undefined
          ? { notificationSound: dbUser.notificationSound }
          : { notificationSound: true }),
        projectsCount: dbUser.projectsCount || 1,
        projectShareCount: dbUser.projectShareCount || 5,
        projectPagesCount: dbUser.projectPagesCount || 5,
        projectEditorsCount: dbUser.projectEditorsCount || 0,
        onboarding: dbUser.onboarding || {},
        emailPreferences: dbUser.emailPreferences || defaultEmailPreferences,
        projectsEditors: dbUser.projectsEditors || 0,
        ...(dbUser.stripeCustomerID
          ? { stripeCustomerID: dbUser.stripeCustomerID }
          : {}),
        ...(dbUser.sumoLingID ? { sumoLingID: dbUser.sumoLingID } : {}),
        ...(dbUser.privilege ? { privilege: dbUser.privilege } : {}),
        ...(dbUser.pendingEmail ? { pendingEmail: dbUser.pendingEmail } : {}),
      }),
    );
    yield put(allProjectsRequest());
  } else if (isRetry === false) {
    // User does not exist in db. Retry in five seconds.
    // eslint-disable-next-line no-console
    console.error('User details not found. Retrying in 5...');
    yield delay(5000);
    yield fetchCurrentUserSaga({ payload, isRetry: true });
  } else {
    /* captureMessage(
      `Error: UID ${payload} not found in DB even after second attempt (sent by fetchCurrentUserSaga)`,
    ); */
    currentUser = yield select(selectors.user);
    yield put(
      signInSuccess({
        projectsCount: 1,
        projectShareCount: 5,
        projectPagesCount: 5,
        projectEditorsCount: 0,
        onboarding: {},
        emailPreferences: defaultEmailPreferences,
      }),
    );
    yield put(allProjectsRequest());
  }
}

export function* watchFetchCurrentUser() {
  yield takeLatest(actions.FETCH_CURRENT_USER, fetchCurrentUserSaga);
}

function* notificationsChannel(notificationsSnap) {
  const {
    user,
    notifications: { loading },
  } = yield select(selectors.user);
  let notifications = [];

  if (!user) return;

  notifications = notificationsSnap.docs.map((i) => ({
    id: i.id,
    ...i.data(),
  }));

  if (notifications.length > 0) {
    const deleteNotifications = [];

    if (notifications.some((i) => i.type === 'web-inspect')) {
      const inpsectNotifications = notifications.filter(
        (i) => i.type === 'web-inspect',
      );
      const inspectGroups = Array.from(
        new Set(inpsectNotifications.map((i) => JSON.stringify(i.context))),
      ).map((i) => JSON.parse(i));
      const finalInspectChanges = [];

      inspectGroups.forEach((i) => {
        const similar = inpsectNotifications.filter(
          (j) => JSON.stringify(j.context) === JSON.stringify(i),
        );
        /* eslint-disable indent */
        const totalChanges =
          similar.length > 1
            ? similar.reduce(
                (a, c) => a.inspectChangesLength + c.inspectChangesLength,
              )
            : 1;
        /* eslint-enable indent */

        finalInspectChanges.push({
          ...similar[0],
          inspectChangesLength: totalChanges,
        });
      });

      notifications = notifications.filter((i) => i.type !== 'web-inspect');
      notifications = [...notifications, ...finalInspectChanges];
    }

    let users = Array.from(
      new Set(notifications.map((i) => i.context.userID)),
    ).filter((i) => Boolean(i));
    let projects = Array.from(
      new Set(notifications.map((i) => i.context.projectID)),
    ).filter((i) => Boolean(i));
    let pages = Array.from(
      new Set(
        notifications
          .map((i) =>
            i.context.pageID
              ? `${i.context.projectID},${i.context.pageID}`
              : null,
          )
          .filter((i) => i),
      ),
    ).filter((i) => Boolean(i));

    users = yield all(
      users.map((i) =>
        getDoc(doc(db, 'users', i)).then((snap) => ({
          uid: snap.id,
          ...snap.data(),
        })),
      ),
    );

    projects = yield all(
      projects.map((i) =>
        getDoc(doc(db, 'projects', i)).then((snap) =>
          snap.exists() ? { id: snap.id, ...snap.data() } : null,
        ),
      ),
    );
    projects = projects.filter((i) => Boolean(i));

    pages = yield all(
      pages.map((i) =>
        getDoc(
          doc(db, 'projects', i.split(',')[0], 'pages', i.split(',')[1]),
        ).then((snap) =>
          snap.exists() ? { id: snap.id, ...snap.data() } : null,
        ),
      ),
    );

    pages = pages.filter((i) => Boolean(i));

    // Get user details.
    notifications = notifications.map((i) => {
      const foundUser = users.find((j) => j.uid === i.context.userID);

      return {
        ...i,
        context: {
          ...i.context,
          photoURL: foundUser.photoURL,
          userName:
            foundUser.displayName && foundUser.displayName.length > 0
              ? foundUser.displayName
              : foundUser.email,
        },
      };
    });

    // Remove deleted projects.
    notifications = notifications.filter((i) => {
      if (!i.context.projectID) return true;

      const bool = projects.some((p) => p.id === i.context.projectID);

      if (bool === false) deleteNotifications.push(i);

      return bool;
    });

    // Get project title.
    notifications = notifications.map((i) => {
      const foundProject = projects.find((j) => j.id === i.context.projectID);

      return {
        ...i,
        context: { ...i.context, projectTitle: foundProject?.title ?? '' },
      };
    });

    // Remove deleted pages.
    notifications = notifications.filter((i) => {
      if (!i.context.pageID) return true;

      const bool = pages.some((p) => p.id === i.context.pageID);

      if (bool === false) deleteNotifications.push(i);

      return bool;
    });

    // Get page title.
    notifications = notifications.map((i) => {
      if (!i.context.pageID) return i;

      const foundPage = pages.find((j) => j.id === i.context.pageID);

      return {
        ...i,
        context: { ...i.context, pageTitle: foundPage?.title ?? '' },
      };
    });

    yield put(notificationsSuccess(notifications));

    if (deleteNotifications.length > 0) {
      const batch = writeBatch(db);

      deleteNotifications.forEach((i) =>
        batch.delete(doc(db, 'users', user.uid, 'notifications', i.id)),
      );

      yield batch.commit();
    }
  }

  if (loading === true) yield put(toggleNotificationsLoading(false));
  else if (
    notifications.some(({ isNotified = false }) => isNotified === false)
  ) {
    if (
      window.location.pathname.endsWith('comment') ||
      window.location.pathname.endsWith('preview') ||
      window.location.pathname.endsWith('inspect')
    ) {
      const sorted = notifications.sort(
        (a, b) => b.createdAt.valueOf() - a.createdAt.valueOf(),
      );

      const n = sorted[0];

      if (!(n.isShown ?? false)) {
        toast(getContent(n), {
          autoClose: 3000,
          closeOnClick: true,
          onClick: () => {
            window.location.href = getLink(n);
          },
          onClose: () => {
            (async () => {
              await updateDoc(
                doc(db, 'users', user.uid, 'notifications', n.id),
                {
                  isShown: true,
                },
              );
            })();
          },
        });

        if (user.notificationSound ?? true) {
          const audio = new Audio('/assets/audio/notification.mp3');

          yield audio.play();
        }

        yield updateDoc(doc(db, 'users', user.uid, 'notifications', n.id), {
          isShown: true,
        });
      }
    }
  }
}

function getNotificationType(
  u,
  type,
  mentionedUsers,
  commentReplyThreadInitiator,
  commentThreadMembers,
) {
  let notificationType = null;

  if (type.includes('comment')) {
    let prefix = 'web';

    if (type.includes('illustrator')) prefix = 'illustrator';
    notificationType = `${prefix}-comment`;

    if (type.includes('reply')) {
      if (commentReplyThreadInitiator === u)
        notificationType = `${prefix}-comment-reply`;

      if (
        commentThreadMembers &&
        commentThreadMembers.length > 0 &&
        commentThreadMembers.some((i) => i === u)
      )
        notificationType = `${prefix}-comment-reply-mention`;
    }

    if (type.includes('mention') && mentionedUsers.some((i) => i === `{@${u}}`))
      notificationType = `${prefix}-comment-mention`;
  }

  return notificationType;
}

function* pushNotificationSaga({ payload }) {
  const { user } = yield select(selectors.user);
  const timestamp = Timestamp.now();
  let page = yield select(currentPage);
  let project = yield select(currentProject);
  const {
    type,
    context,
    mentionedUsers,
    commentReplyThreadInitiator,
    commentThreadMembers,
    inspectChangesLength,
    sharedWith,
    activity = {},
    teamMembers,
    collaborators = [],
  } = payload;
  let notifyUsers = [];

  let currentUserUID = user.uid;

  if (!currentUserUID)
    currentUserUID =
      window.localStorage.getItem('ruttl_share_guest') || 'public';

  if (
    type.includes('comment') ||
    type.includes('inspect') ||
    type.includes('share')
  ) {
    if (project.id !== context.projectID) {
      project = yield getDoc(doc(db, 'projects', context.projectID));
      project = project.data();
    }
  }

  if (type.includes('comment') || type.includes('inspect')) {
    if (page.id !== context.pageID) {
      page = yield getDoc(
        doc(db, 'projects', context.projectID, 'pages', context.pageID),
      );
      page = page.data();
    }
  }

  if (type.includes('comment')) {
    if (project.users && project.users.length > 0)
      notifyUsers = project.users
        .filter((u) => u.uid !== currentUserUID)
        .map((i) => i.uid);

    notifyUsers = notifyUsers.map((u) => ({
      createdFor: u,
      type: getNotificationType(
        u,
        type,
        mentionedUsers,
        commentReplyThreadInitiator,
        commentThreadMembers,
      ),
      context: {
        ...context,
        userID: currentUserUID,
      },
      isSeen: false,
      createdAt: timestamp,
    }));

    if (
      notifyUsers.some(
        (i) =>
          i.type === 'web-comment-reply' ||
          i.type === 'web-comment-reply-mention' ||
          i.type === 'web-comment-mention',
      )
    )
      notifyUsers = notifyUsers.filter(
        (i) =>
          i.type === 'web-comment-reply' ||
          i.type === 'web-comment-reply-mention' ||
          i.type === 'web-comment-mention',
      );

    if (
      notifyUsers.some(
        (i) =>
          i.type === 'illustration-comment-reply' ||
          i.type === 'illustration-comment-reply-mention' ||
          i.type === 'illustration-comment-mention',
      )
    )
      notifyUsers = notifyUsers.filter(
        (i) =>
          i.type === 'illustration-comment-reply' ||
          i.type === 'illustration-comment-reply-mention' ||
          i.type === 'illustration-comment-mention',
      );
  }

  if (type.includes('inspect')) {
    if (project.users && project.users.length > 0)
      notifyUsers = project.users
        .filter((u) => u.access === 'edit' && u.uid !== currentUserUID)
        .map((i) => i.uid);

    notifyUsers = notifyUsers.map((u) => ({
      createdFor: u,
      type: 'web-inspect',
      context: {
        ...context,
        ...activity,
        userID: currentUserUID,
      },
      isSeen: false,
      createdAt: timestamp,
      inspectChangesLength,
    }));
  }

  if (type.includes('share')) {
    notifyUsers.push({
      createdFor: sharedWith,
      type: 'project-share',
      context: {
        ...context,
        userID: currentUserUID,
      },
      isSeen: false,
      createdAt: timestamp,
    });
  }

  if (type.includes('page')) {
    if (project.users && project.users.length > 0)
      notifyUsers = project.users
        .filter((u) => u.uid !== currentUserUID)
        .map((i) => i.uid);

    notifyUsers = notifyUsers.map((u) => ({
      createdFor: u,
      type: 'web-page-add',
      context: {
        ...context,
        userID: currentUserUID,
      },
      isSeen: false,
      createdAt: timestamp,
    }));
  }

  if (type === 'team-member-add') {
    notifyUsers = (teamMembers ?? []).map(({ uid }) => ({
      createdFor: uid,
      type,
      context: {
        ...context,
        userID: user.uid,
        displayName: user.displayName,
      },
      isSeen: false,
      createdAt: timestamp,
    }));
  }

  if (type === 'team-member-access') {
    notifyUsers = (teamMembers ?? []).map(({ uid, access }) => ({
      createdFor: uid,
      type,
      context: {
        ...context,
        access,
        userID: user.uid,
        displayName: user.displayName,
      },
      isSeen: false,
      createdAt: timestamp,
    }));
  }

  if (type === 'make-approver') {
    notifyUsers.push({
      createdFor: context.approverID,
      type,
      context: {
        ...context,
        userID: user.uid,
        displayName: user.displayName,
      },
      isSeen: false,
      createdAt: timestamp,
    });
  }

  if (type === 'page-approved') {
    notifyUsers = (collaborators ?? []).map(({ uid }) => ({
      createdFor: uid,
      type,
      context: {
        ...context,
        userID: user.uid,
        displayName: user.displayName,
      },
      isSeen: false,
      createdAt: timestamp,
    }));
  }

  if (type === 'page-unapproved') {
    notifyUsers = (collaborators ?? []).map(({ uid }) => ({
      createdFor: uid,
      type,
      context: {
        ...context,
        userID: user.uid,
        displayName: user.displayName,
      },
      isSeen: false,
      createdAt: timestamp,
    }));
  }

  if (type === 'team-member-left') {
    notifyUsers = (teamMembers ?? []).map((uid) => ({
      createdFor: uid,
      type,
      context: {
        ...context,
        userID: user.uid,
        displayName: user.displayName,
      },
      isSeen: false,
      createdAt: timestamp,
    }));
  }

  yield all(
    notifyUsers
      .filter(({ createdFor }) => Boolean(createdFor))
      .map((i) => {
        const { createdFor, ...rest } = i;

        return addDoc(collection(db, 'users', createdFor, 'notifications'), {
          ...rest,
          emailSent: false,
        });
      }),
  );
}

function* cancelNotificatiosChannel(channel) {
  yield take(actions.CANCEL_LISTEN_NOTIFICATIONS);
  channel.close();
}

function* listenNotificationsSaga() {
  const { user } = yield select(selectors.user);
  const channel = yield call(
    createDocumentChangeChannel,
    collection(db, 'users', user.uid, 'notifications'),
  );

  yield takeEvery(channel, notificationsChannel);
  yield fork(cancelNotificatiosChannel, channel);
}

export function* watchListenNotifications() {
  yield takeLatest(actions.LISTEN_NOTIFICATIONS, listenNotificationsSaga);
}

export function* watchPushNotification() {
  yield takeLatest(actions.PUSH_NOTIFICATION, pushNotificationSaga);
}

function* seenNotificationSaga({ payload }) {
  const { notifications, user } = yield select(selectors.user);
  const batch = writeBatch(db);
  let commentIDs = [];

  if (typeof payload === 'string') commentIDs.push(payload);
  if (Array.isArray(payload)) commentIDs = [...payload];

  yield put(
    notificationsSuccess(
      notifications.all.map((i) => {
        if (commentIDs.some((c) => c === i.id)) {
          const newIsSeen = typeof i.isSeen === 'undefined' ? true : !i.isSeen;

          batch.update(doc(db, 'users', user.uid, 'notifications', i.id), {
            isSeen: newIsSeen,
          });
          return { ...i, isSeen: newIsSeen };
        }
        return i;
      }),
    ),
  );

  yield batch.commit();
}

export function* watchSeenNotification() {
  yield takeLatest(actions.SEEN_NOTIFICATION, seenNotificationSaga);
}

function* clearAllNotificationsSaga() {
  const { notifications, user } = yield select(selectors.user);
  const batch = writeBatch(db);

  notifications.all
    .filter((i) => i.isSeen === false)
    .forEach((i) => {
      batch.update(doc(db, 'users', user.uid, 'notifications', i.id), {
        isSeen: true,
      });
    });

  yield put(
    notificationsSuccess(
      notifications.all.map((i) => ({ ...i, isSeen: true })),
    ),
  );
  yield batch.commit();
}

export function* watchClearAllNotifications() {
  yield takeLatest(actions.CLEAR_ALL_NOTIFICATIONS, clearAllNotificationsSaga);
}

function* forgotPasswordSaga({ payload }) {
  try {
    yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/passwordReset`,
      { email: payload },
    );
  } catch (error) {
    yield put(forgotPasswordUpdate({ passwordSent: false, error }));
    return;
  }

  yield put(forgotPasswordUpdate({ passwordSent: true, error: '' }));
  logEvent(analytics, 'user_forgot_password_request', {
    reset_email: payload || null,
  });
}

export function* watchForgotPassword() {
  yield takeLatest(actions.FORGOT_PASSWORD, forgotPasswordSaga);
}

function* verifyPasswordResetCodeSaga({ payload }) {
  try {
    const data = yield verifyPasswordResetCode(auth, payload);

    yield put(
      userActionUpdate({
        passwordResetEmail: data,
        passwordResetSuccess: false,
        error: null,
      }),
    );
  } catch (error) {
    yield put(
      userActionUpdate({
        passwordResetEmail: null,
        passwordResetSuccess: false,
        error: error.code,
      }),
    );
  }
}

export function* watchVerifyPasswordResetCode() {
  yield takeLatest(
    actions.VERIFY_PASSWORD_RESET_CODE,
    verifyPasswordResetCodeSaga,
  );
}

function* confirmPasswordResetSaga({ payload: { code, newPassword, email } }) {
  try {
    yield confirmPasswordReset(auth, code, newPassword);
    yield put(
      userActionUpdate({
        passwordResetEmail: null,
        passwordResetSuccess: true,
        error: null,
      }),
    );
    logEvent(analytics, 'user_forgot_password_success', {
      reset_email: email || null,
    });
  } catch (error) {
    yield put(
      userActionUpdate({
        passwordResetEmail: email,
        passwordResetSuccess: false,
        error: error.code,
      }),
    );
  }
}

export function* watchConfirmPasswordReset() {
  yield takeLatest(actions.CONFIRM_PASSWORD_RESET, confirmPasswordResetSaga);
}

function* sendFeedbackSaga({ payload }) {
  const { isContact } = payload;

  try {
    yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendFeedback`,
      payload,
    );
    yield put(
      feedbackUpdate({
        sent: true,
        errors: null,
      }),
    );
    if (isContact) {
      yield put(
        contactUpdate({
          showContactModal: false,
          success: true,
        }),
      );
      yield put(
        showSnackbar("Your message has been sent. We'll get in touch soon."),
      );
    }
    yield delay(1000);
    yield put(
      feedbackUpdate({
        sent: false,
        errors: null,
      }),
    );
    if (isContact) {
      yield put(
        contactUpdate({
          showContactModal: false,
          success: false,
        }),
      );
      yield delay(4000);
      yield put(hideSnackbar());
    }
  } catch (error) {
    yield put(
      feedbackUpdate({
        sent: true,
        errors: error.code,
      }),
    );
    yield delay(1000);
    yield put(
      feedbackUpdate({
        sent: false,
        errors: error.code,
      }),
    );
  }
}

export function* watchSendFeedback() {
  yield takeLatest(actions.SEND_FEEDBACK, sendFeedbackSaga);
}

function* resendVerificationEmailSaga() {
  try {
    yield sendEmailVerification(auth.currentUser);
    yield put(showSnackbar('Email verification email has been sent.'));
    logEvent(analytics, 'user_email_confirm_request', {
      event_user: (auth && auth.currentUser && auth.currentUser.uid) || null,
    });
    yield delay(5000);
    yield put(hideSnackbar());
  } catch (error) {
    // Handle error.
  }
}

export function* watchResendVerificationEmail() {
  yield takeLatest(
    actions.RESEND_VERIFICATION_EMAIL,
    resendVerificationEmailSaga,
  );
}

function* verifyEmailSaga({ payload, navigate }) {
  const { user } = yield select(selectors.user);

  try {
    yield applyActionCode(auth, payload);
    // Firebase Auth does not automatically refresh `auth.currentUser.emailVerified`.
    yield reload(auth.currentUser);
    yield put(showSnackbar('Your email has been verified.'));
    navigate(user && user.uid ? '/projects' : '/');

    yield axios.post(
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
      {
        from: `Team ruttl <support@ruttl.com>`,
        to: user.email,
        subject: `Welcome to ruttl!`,
        template: window.innerWidth < 979 ? 'user-signup-mobile' : 'welcome',
        templateVariables: JSON.stringify({
          unsubscribeURL: `${window.location.origin}/unsubscribe?id=${user.uid}&type=marketing`,
          settingsURL: `${window.location.origin}/profile`,
        }),
      },
    );
    logEvent(analytics, 'user_email_confirm_success', {
      event_user: (auth && auth.currentUser && auth.currentUser.uid) || null,
    });
    yield delay(5000);
    yield put(hideSnackbar());
  } catch (error) {
    yield put(
      userActionUpdate({
        passwordResetEmail: null,
        passwordResetSuccess: false,
        error: error.code,
      }),
    );
  }
}

export function* watchVerifyEmail() {
  yield takeLatest(actions.VERIFY_EMAIL, verifyEmailSaga);
}

function* contactSaga({ payload }) {
  try {
    yield axios.post(
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/contact`,
      payload,
    );
    yield put(
      contactUpdate({
        showContactModal: false,
        success: true,
      }),
    );
    yield put(
      showSnackbar("Your message has been sent. We'll get in touch soon."),
    );
    yield delay(100);
    yield put(
      contactUpdate({
        showContactModal: false,
        success: false,
      }),
    );
    yield delay(5000);
    yield put(hideSnackbar());
  } catch (error) {
    yield put(
      contactUpdate({
        showContactModal: true,
        success: false,
      }),
    );
  }
}

export function* watchContact() {
  yield takeLatest(actions.CONTACT, contactSaga);
}

function* sendProRequestSaga({ payload, callback }) {
  try {
    yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendProRequest`,
      payload,
    );
    yield put(
      showSnackbar("Your request has been sent. We'll get in touch soon."),
    );
    yield callback({ type: 'success' });
    yield delay(5000);
    yield put(hideSnackbar());
  } catch (error) {
    yield callback({ type: 'error', error });
  }
}

export function* watchSendProRequest() {
  yield takeLatest(actions.SEND_PRO_REQUEST, sendProRequestSaga);
}

function* onboardingUpdateSaga({ payload }) {
  try {
    const { user } = yield select(selectors.user);
    const newOnboarding = {};

    if (
      typeof user.onboarding === 'object' &&
      Object.keys(user.onboarding).length > 0
    )
      Object.assign(newOnboarding, user.onboarding);

    newOnboarding[payload] = true;
    yield put(signInSuccess({ onboarding: newOnboarding }));
    yield updateDoc(doc(db, 'users', user.uid), { onboarding: newOnboarding });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
}

export function* watchOnboardingUpdate() {
  yield takeLatest(actions.ONBOARDING_UPDATE, onboardingUpdateSaga);
}

function* toggleEmailPreferenceSaga({ payload }) {
  const { user } = yield select(selectors.user);

  const emailPreferences = {
    ...defaultEmailPreferences,
    ...user.emailPreferences,
  };

  emailPreferences[payload] = !emailPreferences[payload];

  if (payload === 'blogUpdatesBugFixes') {
    if (emailPreferences.blogUpdatesBugFixes === false) {
      // don't wait here
      axios.post(`${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`, {
        from: `Team ruttl <support@ruttl.com>`,
        to: 'rishab.s@brucira.com', // process.env.REACT_APP_MARKETING_UNSUBSCRIBE_RECIPIENT,
        subject: 'Unsubscribe from ruttl’s audience on MailChimp',
        text: `Please unsubscribe: ${user.email}`,
      });
    } else {
      // don't wait here
      axios.post(`${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`, {
        from: `Team ruttl <support@ruttl.com>`,
        to: 'rishab.s@brucira.com', // process.env.REACT_APP_MARKETING_UNSUBSCRIBE_RECIPIENT,
        subject: 'Subscribe to ruttl’s audience on MailChimp',
        text: `Please add to subscribers: ${user.email}, ignore if already exists`,
      });
    }
  }

  yield put(signInSuccess({ emailPreferences }));
  yield updateDoc(doc(db, 'users', user.uid), { emailPreferences });
}

export function* watchToggleEmailPreference() {
  yield takeLatest(actions.TOGGLE_EMAIL_PREFERENCE, toggleEmailPreferenceSaga);
}

function* deleteUserSaga({ navigate }) {
  try {
    yield axios.get(
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/deleteUser`,
      {
        headers: { token: auth.currentUser.accessToken },
      },
    );
    // Firebase Auth does not automatically clears `auth.currentUser`.
    yield auth.signOut();
    yield put(signOutSuccess());
    yield put(resetProject());
    yield put(resetIllustration());
    yield put(resetWeb());
    yield put(resetTeam());
    setUserId(analytics, '');
    yield put(cancelProjectsListener());
    yield put(cancelTeamsListener());
    navigate('/');
    yield put(showSnackbar('Account deleted.'));
    yield delay(4000);
    yield put(hideSnackbar());
  } catch (error) {
    //
  }
}

export function* watchDeleteUser() {
  yield takeLatest(actions.DELETE_USER, deleteUserSaga);
}

function* notificationsOpenendSaga() {
  const { notifications, user } = yield select(selectors.user);

  if (notifications.all.length > 0) {
    const unNotified = notifications.all.filter(
      ({ isNotified }) => !isNotified,
    );

    if (unNotified.length > 0) {
      const batch = writeBatch(db);
      const newNotifications = [];

      unNotified.forEach((n) => {
        batch.update(doc(db, 'users', user.uid, 'notifications', n.id), {
          isNotified: true,
        });
        newNotifications.push({ ...n, isNotified: true });
      });

      yield put(notificationsSuccess(newNotifications));
      yield batch.commit();
    }
  }
}

export function* watchNotificationsOpened() {
  yield takeLatest(actions.NOTIFICATIONS_OPENED, notificationsOpenendSaga);
}

function* fetchCurrentIntegrationsWorkerSaga() {
  const { user } = yield select(selectors.user);
  const snapshot = yield getDocs(
    collection(db, 'users', user.uid, 'integrations'),
  );
  const integrations = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  yield put(fetchCurrentIntegrationsSuccess(integrations));
}

export function* fetchCurrentIntegrationsWatcherSaga() {
  yield takeLatest(
    actions.FETCH_CURRENT_INTEGRATIONS_REQUEST,
    fetchCurrentIntegrationsWorkerSaga,
  );
}

function* cancelSubscriptionSaga({ callback }) {
  const { user, subscriptions } = yield select(selectors.user);

  if (!user.stripeCustomerID) return;

  try {
    const { data: subscription } = yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeCancelSubscription`,
      { id: subscriptions[0].id },
    );

    yield put(subscriptionsSuccess(subscription ? [subscription] : []));
    yield put(billingDetailsRequest());
    callback({
      status: subscription.status,
      data: subscription,
    });
  } catch (error) {
    callback({ status: 'error', data: error.response.data });
  }
}

export function* watchCancelSubscription() {
  yield takeLatest(actions.CANCEL_SUBSCRIPTION, cancelSubscriptionSaga);
}

function* updateSubscriptionSaga({ payload, callback }) {
  try {
    let { data } = yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeUpdateSubscription`,
      payload,
    );

    if (data.status === 'active' && !data.pending_update)
      callback({ type: 'success' });
    else if (data.status === 'past_due' || data.pending_update) {
      data = yield checkIncompleteSubscription(data, data.customer);

      callback({ type: 'past_due' });
    } else callback({ type: data.status });
    yield put(subscriptionsSuccess([data]));
    yield put(billingDetailsSuccess({}));
    yield put(billingDetailsRequest());
  } catch (error) {
    callback({ type: 'error', error: error.toString() });
  }
}

export function* watchUpdateSubscription() {
  yield takeLatest(actions.UPDATE_SUBSCRIPTION, updateSubscriptionSaga);
}

function* billingDetailsRequestSaga() {
  const { user, subscriptions } = yield select(selectors.user);
  const activeSubscription =
    (subscriptions.length > 0 &&
      subscriptions.find(({ status }) => status === 'active')) ||
    null;
  let billingDetails = {};

  if (!user.stripeCustomerID && !user.sumoLingID) {
    yield put(billingDetailsSuccess({}));
    return;
  }

  try {
    if (typeof user.sumoLingID === 'string') {
      const sumoLingSnap = yield getDoc(doc(db, 'sumo-lings', user.sumoLingID));

      if (sumoLingSnap.get('isDisabled') === false)
        billingDetails.sumoLing = {
          ...sumoLingSnap.data(),
          id: sumoLingSnap.id,
        };
    }

    if (!billingDetails.sumoLing && typeof user.stripeCustomerID === 'string') {
      const { data } = yield call(
        axios.post,
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeRetrieveCustomer`,
        {
          customerID: user.stripeCustomerID,
          ...(activeSubscription?.default_payment_method
            ? { paymentMethod: activeSubscription.default_payment_method }
            : {}),
        },
      );

      billingDetails = data;
    }
    yield put(billingDetailsSuccess(billingDetails));
  } catch (err) {
    // Error
  }
}

export function* watchBillingDetailsRequest() {
  yield takeLatest(actions.BILLING_DETAILS_REQUEST, billingDetailsRequestSaga);
}

function* undoCancelSubscriptionSaga() {
  const { subscriptions } = yield select(selectors.user);
  const activeSubscription =
    (subscriptions.length > 0 &&
      subscriptions.find(
        ({ status, cancel_at_period_end }) =>
          status === 'active' && cancel_at_period_end === true,
      )) ||
    null;

  if (!activeSubscription) return;

  try {
    const { data } = yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeUndoCancelSubscription`,
      { id: activeSubscription.id },
    );

    yield put(subscriptionsSuccess([data]));
    yield put(showSnackbar('Your subscription will not cancel automatically.'));
    yield put(billingDetailsSuccess({}));
    yield put(billingDetailsRequest());
    yield delay(5000);
    yield put(hideSnackbar());
  } catch (err) {
    // callback({ type: 'error' });
  }
}

export function* watchUndoCancelSubscription() {
  yield takeLatest(
    actions.UNDO_CANCEL_SUBSCRIPTION,
    undoCancelSubscriptionSaga,
  );
}

function* toggleNotificationSound() {
  const { user } = yield select(selectors.user);

  yield put(signInSuccess({ notificationSound: !user.notificationSound }));

  yield updateDoc(doc(db, 'users', user.uid), {
    notificationSound: !user.notificationSound,
  });
}

export function* watchToggleNotificationSound() {
  yield takeLatest(actions.TOGGLE_NOTIFICATION_SOUND, toggleNotificationSound);
}

function* resendEmailChangeVerifyEmailSaga({ callback }) {
  const token = yield auth.currentUser.getIdToken();

  yield call(axios.post, `${window.location.origin}/email-change`, {
    token,
  });

  yield put(showSnackbar('Verification email sent.'));
  yield delay(5000);
  yield put(hideSnackbar());

  callback();
}

export function* watchResendEmailChangeVerifyEmail() {
  yield takeLatest(
    actions.RESEND_EMAIL_CHANGE_VERIFY_EMAIL,
    resendEmailChangeVerifyEmailSaga,
  );
}

function* cancelSubcriptionPendingUpdateSaga({ payload }) {
  const { user } = yield select(selectors.user);

  yield put(showSnackbar('Cancelling subscription update...'));

  try {
    yield call(
      axios.post,
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/stripeCancelSubscriptionPendingUpdate`,
      { in: payload },
    );
    yield put(showSnackbar('Subscription update cancelled.'));
    yield put(subscriptionsSuccess([]));
    yield put(billingDetailsSuccess({}));
    if (user?.stripeCustomerID) {
      yield call(getUserStripeSubscriptions, user.stripeCustomerID);
      yield put(billingDetailsRequest());
    }
  } catch (err) {
    yield put(showSnackbar('Something went wrong.'));
    //
  }

  yield delay(5000);
  yield put(hideSnackbar());
}

export function* watchCancelSubcriptionPendingUpdate() {
  yield takeLatest(
    actions.CANCEL_SUBSCRIPTION_PENDING_UPDATE,
    cancelSubcriptionPendingUpdateSaga,
  );
}
